import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { FaArrowLeft, FaHotel } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaBed,
  FaUser,
  FaPhone,
  FaCalendarAlt,
  FaReceipt,
  FaEye,
  FaCheck,
  FaTimes,
  FaArrowRight,
  FaShieldAlt,
  FaTag,
  FaCreditCard,
  FaMoneyBillWave,
  FaUtensils,
} from 'react-icons/fa';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const RoomUpgrade = () => {
  // Get reservation from session storage
  const reservation = JSON.parse(sessionStorage.getItem('selectedReservation') || '{}');

  const [formData, setFormData] = useState({
    fullName: `${reservation?.firstname || ''} ${reservation?.lastname || ''}`.trim(),
    bookingId: reservation?.bookingid || '',
    phone: reservation?.phonenumber || '',
    totalGuests: reservation?.numberofguests || '',
    currentRoomType: reservation?.roomtypename || '',
    newRoomType: '',
    checkInDate: reservation?.checkindate ? new Date(reservation.checkindate) : new Date(),
    checkOutDate: reservation?.checkoutdate ? new Date(reservation.checkoutdate) : null,
    newCheckInDate: null,
    newCheckOutDate: null,
  });

  const [roomTypes, setRoomTypes] = useState([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Factors (add-ons) of this reservation, from the modify options API
  const [appliedFactors, setAppliedFactors] = useState([]);
  const [availableFactors, setAvailableFactors] = useState([]);
  const [selectedFactorIds, setSelectedFactorIds] = useState([]);
  const [factorsLoaded, setFactorsLoaded] = useState(false);

  // Result of /availability/check-dates/all for the dates and add-ons currently entered
  const [availability, setAvailability] = useState(null);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  // Fetch room types from roomtypes API (active ones only)
  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/roomtypes/`, {
          method: 'GET',
          headers: { accept: 'application/json' },
        });
        const data = await response.json();
        // /roomtypes/ returns a plain array (not wrapped in { roomtypes: [...] })
        setRoomTypes(Array.isArray(data) ? data : data.roomtypes || []);
      } catch (error) {
        console.error('Error fetching room types:', error);
        toast.error('Failed to load room types');
      }
    };
    fetchRoomTypes();
  }, []);

  // Fetch the reservation's current factors and the factors that can be selected
  useEffect(() => {
    if (!formData.bookingId) return;
    const fetchFactors = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/modify/${formData.bookingId}/options`, {
          method: 'GET',
          headers: { accept: 'application/json' },
        });
        const json = await response.json();
        const options = json.data || {};
        const applied = options.applied_factors || [];
        const available = options.available_factors || [];
        setAppliedFactors(applied);
        setAvailableFactors(available);
        // start with the factors the reservation already has (auto factors are not ticked)
        const selectableIds = available
          .filter((f) => f.factor_type !== 'auto')
          .map((f) => Number(f.factorid));
        setSelectedFactorIds(
          applied.map((f) => Number(f.factorid)).filter((id) => selectableIds.includes(id))
        );
      } catch (error) {
        console.error('Error fetching factors:', error);
        toast.error('Failed to load the reservation add-ons');
      } finally {
        setFactorsLoaded(true);
      }
    };
    fetchFactors();
  }, [formData.bookingId]);

  useEffect(() => {
    setAvailability(null);
  }, [formData.newRoomType, formData.newCheckInDate, formData.newCheckOutDate, selectedFactorIds]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ── Factors ──────────────────────────────────────────────────────────────────
  // Auto factors (e.g. Number of Guest) are priced from the guest count by the backend and
  // are not something staff tick. Applied factors that are not in the selectable list
  // (inactive / not shown to front desk) are kept as they are so they are never dropped.
  const availableFactorIdSet = new Set(availableFactors.map((f) => Number(f.factorid)));
  const autoFactors = appliedFactors.filter((f) => f.factor_type === 'auto');
  const lockedFactors = appliedFactors.filter(
    (f) => f.factor_type !== 'auto' && !availableFactorIdSet.has(Number(f.factorid))
  );
  const selectableFactors = availableFactors.filter((f) => f.factor_type !== 'auto');
  const bedTypeFactors = selectableFactors.filter((f) => f.factor_type === 'bedtype');
  const addonFactors = selectableFactors.filter((f) => f.factor_type !== 'bedtype');

  const toggleFactor = (factor) => {
    const id = Number(factor.factorid);
    setSelectedFactorIds((prev) => {
      if (factor.factor_type === 'bedtype') {
        // only one bed type at a time
        const others = bedTypeFactors.map((f) => Number(f.factorid));
        return [...prev.filter((x) => !others.includes(x)), id];
      }
      return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    });
  };

  // Values are only a hint: the backend prices every factor from factor_room_type_values.
  const toFactorPayload = (f, value) => ({
    factorid: f.factorid,
    factorname: f.factorname,
    value,
    value_type: f.value_type,
    factor_type: f.factor_type,
  });

  const buildPreviousFactors = () =>
    appliedFactors.map((f) => toFactorPayload(f, f.value_applied ?? 0));

  const buildSelectedFactors = () => [
    ...lockedFactors.map((f) => toFactorPayload(f, f.value_applied ?? 0)),
    ...selectableFactors
      .filter((f) => selectedFactorIds.includes(Number(f.factorid)))
      .map((f) => toFactorPayload(f, f.applied_value_for_roomtype ?? 0)),
  ];

  const factorsChanged = () => {
    const before = appliedFactors
      .filter((f) => f.factor_type !== 'auto' && availableFactorIdSet.has(Number(f.factorid)))
      .map((f) => Number(f.factorid))
      .sort();
    const after = [...selectedFactorIds].sort();
    return before.length !== after.length || before.some((id, i) => id !== after[i]);
  };

  // Guest count the reservation was booked with, and the count staff entered. Guest-based
  // factors (e.g. Number of Guest) are priced for the entered count.
  const bookedGuestCount = Number(reservation?.numberofguests) || undefined;

  const guestCount = () => {
    const entered = parseInt(formData.totalGuests, 10);
    return Number.isFinite(entered) && entered > 0 ? entered : bookedGuestCount;
  };

  const guestsChanged = () => bookedGuestCount !== undefined && guestCount() !== bookedGuestCount;

  const factorBody = () => ({
    selected_factors: buildSelectedFactors(),
    previous_factors: buildPreviousFactors(),
    number_of_guests: guestCount(),
    previous_number_of_guests: bookedGuestCount,
  });

  // ── Availability (GET /availability/check-dates/all) ─────────────────────────
  const targetRoomType = formData.newRoomType || formData.currentRoomType;

  const availabilityFor = (roomTypeName) =>
    availability?.roomTypes?.find((rt) => rt.room_type === roomTypeName);

  const availabilityHint = (roomTypeName) => {
    const result = availabilityFor(roomTypeName);
    if (!result) return '';
    if (result.status === 'AVAILABLE') return ` - ${result.available_rooms} available`;
    if (result.status === 'NOT_APPLICABLE') return ' - not offered with the selected add-ons';
    return ' - sold out';
  };

  const availabilityStyle = {
    AVAILABLE: 'bg-green-50 border-green-200 text-green-800',
    SOLD_OUT: 'bg-red-50 border-red-200 text-red-800',
    NOT_APPLICABLE: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  };

  const availabilityLabel = {
    AVAILABLE: 'Available',
    SOLD_OUT: 'Sold out',
    NOT_APPLICABLE: 'Not offered with the selected add-ons',
  };

  const handleCheckAvailability = async () => {
    const checkin = formatDateToYYYYMMDD(formData.newCheckInDate || formData.checkInDate);
    const checkout = formatDateToYYYYMMDD(formData.newCheckOutDate || formData.checkOutDate);
    if (!checkin || !checkout) {
      toast.error('Select the check-in and check-out dates first');
      return;
    }
    if (checkout <= checkin) {
      toast.error('Check-out must be after check-in');
      return;
    }

    // factors a room must offer: the ticked add-ons and the ones kept as booked (not auto factors)
    const factorIds = [...lockedFactors.map((f) => Number(f.factorid)), ...selectedFactorIds];
    const params = new URLSearchParams({ checkin, checkout });
    if (factorIds.length > 0) params.set('factor_ids', factorIds.join(','));

    setIsCheckingAvailability(true);
    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/availability/check-dates/all?${params.toString()}`,
        { method: 'GET', headers: { accept: 'application/json' } }
      );
      const data = await response.json();
      if (response.ok) {
        setAvailability({ checkin, checkout, roomTypes: data.room_types || [] });
      } else {
        toast.error(data.detail || data.message || 'Failed to check availability');
      }
    } catch (error) {
      console.error('Availability error:', error);
      toast.error('Network error while checking availability');
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const factorValueLabel = (f) => {
    const value = Number(f.applied_value_for_roomtype ?? 0);
    if (!value) return '';
    return f.value_type === 'percentage' ? `${value}%` : formatCurrency(value);
  };

  // ── Unavailability gating ─────────────────────────────────────────────────
  // True when availability has been checked AND the target room type (new or current)
  // has 0 available rooms, is sold out, or is not offered with the selected add-ons.
  const isTargetUnavailable = (() => {
    if (!availability) return false;
    const result = availabilityFor(targetRoomType);
    if (!result) return false;

    if (result.status === 'NOT_APPLICABLE') return true;
    if (result.status === 'SOLD_OUT') return true;
    if (typeof result.available_rooms === 'number' && result.available_rooms <= 0) return true;

    return false;
  })();

  const unavailableReason = (() => {
    if (!isTargetUnavailable) return '';
    const result = availabilityFor(targetRoomType);
    if (!result) return '';
    if (result.status === 'NOT_APPLICABLE') {
      return `${result.room_type} is not offered with the selected add-ons.`;
    }
    return `${result.room_type} has no rooms available for the selected dates.`;
  })();

  // ── API error extraction ───────────────────────────────────────────────────
  // The modify/preview endpoints sometimes return `detail` as an object
  // (e.g. ACTIVE_ADJUSTMENTS_BLOCK_MODIFICATION). This normalizes both shapes
  // and returns a friendly, multi-line string.
  const extractApiErrorMessage = (data, fallback = 'Something went wrong') => {
    const detail = data?.detail;

    // Simple string detail
    if (typeof detail === 'string') return detail;

    // Structured detail object
    if (detail && typeof detail === 'object') {
      const parts = [];

      if (detail.message) parts.push(detail.message);

      const adj = detail.adjustments;
      if (adj) {
        if (adj.message) parts.push(adj.message);

        const counts = [];
        if (adj.manual_adjustment_count > 0) {
          counts.push(
            `${adj.manual_adjustment_count} manual adjustment${
              adj.manual_adjustment_count !== 1 ? 's' : ''
            }`
          );
        }
        if (adj.voucher_count > 0) {
          counts.push(`${adj.voucher_count} voucher${adj.voucher_count !== 1 ? 's' : ''}`);
        }
        if (counts.length > 0) parts.push(`Active: ${counts.join(', ')}.`);
      }

      if (detail.action_required) parts.push(detail.action_required);

      return parts.filter(Boolean).join(' ') || fallback;
    }

    // Fallbacks
    return data?.message || fallback;
  };

  // One row per factor for the preview: what it costs per night before and after
  const buildFactorRows = (fx) => {
    const previous = fx?.previous_factors || [];
    const selected = fx?.selected_factors || [];
    const same = (a, b) => Number(a.factorid) === Number(b.factorid);
    const rows = previous.map((p) => {
      const now = selected.find((s) => same(s, p));
      const before = Number(p.per_night_amount ?? 0);
      const after = now ? Number(now.per_night_amount ?? 0) : null;
      let status = 'Unchanged';
      if (after === null) status = 'Removed';
      else if (after < before) status = 'Reduced';
      else if (after > before) status = 'Increased';
      return { id: p.factorid, name: p.factorname, before, after, status };
    });
    selected
      .filter((s) => !previous.some((p) => same(s, p)))
      .forEach((s) =>
        rows.push({
          id: s.factorid,
          name: s.factorname,
          before: null,
          after: Number(s.per_night_amount ?? 0),
          status: 'Added',
        })
      );
    return rows;
  };

  // Format date to YYYY-MM-DD without timezone issues
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handlePreview = async () => {
    // Check if any changes are made
    const hasRoomChange = formData.newRoomType && formData.newRoomType !== formData.currentRoomType;
    const hasCheckInChange =
      formData.newCheckInDate &&
      formatDateToYYYYMMDD(formData.newCheckInDate) !== formatDateToYYYYMMDD(formData.checkInDate);
    const hasCheckOutChange =
      formData.newCheckOutDate &&
      formatDateToYYYYMMDD(formData.newCheckOutDate) !==
        formatDateToYYYYMMDD(formData.checkOutDate);

    if (
      !hasRoomChange &&
      !hasCheckInChange &&
      !hasCheckOutChange &&
      !factorsChanged() &&
      !guestsChanged()
    ) {
      toast.error(
        'Please make at least one change (room type, dates, number of guests, or add-ons)'
      );
      return;
    }

    setIsLoading(true);
    try {
      const requestBody = {
        new_checkin_date: hasCheckInChange
          ? formatDateToYYYYMMDD(formData.newCheckInDate)
          : formatDateToYYYYMMDD(formData.checkInDate),
        new_checkout_date: hasCheckOutChange
          ? formatDateToYYYYMMDD(formData.newCheckOutDate)
          : formatDateToYYYYMMDD(formData.checkOutDate),
        amenities: [],
        room_services: [],
        food_orders: [],
        ...factorBody(),
      };

      // ALWAYS send the room type - either new one if changed, or current one if not changed
      requestBody.new_room_type_name = hasRoomChange
        ? formData.newRoomType
        : formData.currentRoomType;

      const response = await fetch(`${BQ_BASE_URL}/bq/api/modify/${formData.bookingId}/preview`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (response.ok) {
        setPreviewData(responseData.data);
        setIsPreviewModalOpen(true);
      } else {
        const isAdjustmentBlock =
          responseData?.detail?.code === 'ACTIVE_ADJUSTMENTS_BLOCK_MODIFICATION';

        const message = extractApiErrorMessage(responseData, 'Failed to fetch preview');

        if (isAdjustmentBlock) {
          toast.error(message, { autoClose: 8000 });
        } else {
          toast.error(message);
        }
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Network error while fetching preview data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    setIsConfirmationModalOpen(false);
    setIsLoading(true);

    try {
      const params = new URLSearchParams();

      params.append(
        'new_checkin_date',
        formData.newCheckInDate
          ? formatDateToYYYYMMDD(formData.newCheckInDate)
          : formatDateToYYYYMMDD(formData.checkInDate)
      );
      params.append(
        'new_checkout_date',
        formData.newCheckOutDate
          ? formatDateToYYYYMMDD(formData.newCheckOutDate)
          : formatDateToYYYYMMDD(formData.checkOutDate)
      );

      // ALWAYS send the room type - either new one if changed, or current one if not changed
      params.append(
        'new_room_type_name',
        formData.newRoomType && formData.newRoomType !== formData.currentRoomType
          ? formData.newRoomType
          : formData.currentRoomType
      );

      const url = `${BQ_BASE_URL}/bq/api/modify/${formData.bookingId}/modify?${params.toString()}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(factorBody()),
      });

      const data = await response.json();

      // Debug: help identify exactly what the API returned on failure
      console.log('modify status:', response.status, 'ok:', response.ok, 'body:', data);

      if (response.ok) {
        // Clear the selected reservation from session storage
        sessionStorage.removeItem('selectedReservation');

        // Show success toast with the message from API
        toast.success(data.message || 'Reservation modified successfully', {
          onClose: () => {
            // Navigate back to front desk operations after toast closes
            window.location.href =
              '/dashboard/core-modules/property-management-system/front-desk-operations';
          },
        });

        // Reset form
        setFormData((prev) => ({
          ...prev,
          newRoomType: '',
          newCheckInDate: null,
          newCheckOutDate: null,
        }));
      } else {
        // 409 ACTIVE_ADJUSTMENTS_BLOCK_MODIFICATION and other structured
        // error responses come back with `detail` as an object, not a string.
        const isAdjustmentBlock = data?.detail?.code === 'ACTIVE_ADJUSTMENTS_BLOCK_MODIFICATION';

        const message = extractApiErrorMessage(data, 'Failed to modify reservation');

        console.error('Modify failed:', message, data);

        // Longer autoClose so staff can read the full instructions.
        toast.error(message, { autoClose: 8000 });

        // Optional: if you want to scroll/reopen the confirm modal for another attempt,
        // you could call setIsConfirmationModalOpen(true) here.
        void isAdjustmentBlock; // kept for future conditional UI
      }
    } catch (error) {
      console.error('Modification error:', error);
      toast.error('An error occurred while modifying the reservation');
    } finally {
      setIsLoading(false);
    }
  };
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Handle YYYY-MM-DD format directly
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Don't render if no booking selected
  if (!formData.bookingId) {
    return (
      <div className="p-6 bg-white min-h-screen">
        <div className="max-w-6xl mx-auto mt-8 p-8 bg-yellow-50 rounded-lg text-center">
          <p className="text-yellow-800 font-semibold">
            No reservation selected. Please select a reservation from the room management page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <ToastContainer
        position="top-right"
        autoClose={8000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 99999 }}
      />

      {/* Full-width wrapper with responsive padding */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        <NavLink
          to="/dashboard/core-modules/property-management-system/front-desk-operations"
          className="inline-flex items-center text-gray-400 hover:text-black mb-4"
        >
          <FaArrowLeft className="mr-3 text-md" />
          <span className="text-base sm:text-lg">Back to Front Desk Operations</span>
        </NavLink>

        {/* Header Section */}
        <header className="text-center mb-8 mt-6">
          <div className="flex items-center justify-center space-x-3 mb-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <FaBed className="text-2xl text-gray-700" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Modify Reservation</h1>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed text-sm sm:text-base px-2">
            Update room type, check-in date, or check-out date. Preview all changes before
            confirming.
          </p>
          <div className="w-24 h-1 bg-gray-300 mx-auto mt-4 rounded-full"></div>
        </header>
      </div>

      {/* Main Form Section — full width */}
      <div className="w-full px-4 sm:px-6 lg:px-8 pb-10">
        <div className="w-full bg-gray-50 rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* Guest Information */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                <FaUser className="text-gray-500" />
                <span>Guest Name</span>
              </label>
              <input
                value={formData.fullName}
                readOnly
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* Reservation Number */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">Reservation Number</label>
              <input
                value={formData.bookingId}
                readOnly
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* Phone Number */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                <FaPhone className="text-gray-500" />
                <span>Phone Number</span>
              </label>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
              />
            </div>

            {/* Number of Guests */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">Number of Guests</label>
              <input
                name="totalGuests"
                type="number"
                min="1"
                value={formData.totalGuests}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
              />
              <p className="text-xs text-gray-500 mt-1">
                Used to price guest-based add-ons in the preview.
              </p>
            </div>

            {/* Current Room Type */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                <FaBed className="text-gray-500" />
                <span>Current Room Type</span>
              </label>
              <input
                value={formData.currentRoomType}
                readOnly
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* New Room Type - Optional */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">
                New Room Type <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <select
                name="newRoomType"
                value={formData.newRoomType}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
              >
                <option value="">Same Room Type</option>
                {roomTypes
                  .filter((rt) => rt.is_active !== false)
                  .filter((rt) => rt.roomtypename !== formData.currentRoomType)
                  .map((roomType) => (
                    <option key={roomType.roomtypeid} value={roomType.roomtypename}>
                      {roomType.roomtypename}
                      {availabilityHint(roomType.roomtypename)}
                    </option>
                  ))}
              </select>
            </div>

            {/* Current Check-In Date */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">
                Current Check-In Date
              </label>
              <input
                type="text"
                value={formatDisplayDate(formatDateToYYYYMMDD(formData.checkInDate))}
                readOnly
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* New Check-In Date - Optional */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">
                New Check-In Date <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <DatePicker
                selected={formData.newCheckInDate}
                onChange={(date) => setFormData({ ...formData, newCheckInDate: date })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
                placeholderText="Select new check-in date"
                dateFormat="MMM dd, yyyy"
                minDate={new Date()}
                maxDate={formData.newCheckOutDate || formData.checkOutDate}
                popperPlacement="bottom-start"
                isClearable
                showPopperArrow={false}
                calendarClassName="custom-calendar"
              />
            </div>

            {/* Current Check-Out Date */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">
                Current Check-Out Date
              </label>
              <input
                type="text"
                value={formatDisplayDate(formatDateToYYYYMMDD(formData.checkOutDate))}
                readOnly
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* New Check-Out Date - Optional */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-2">
                New Check-Out Date <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <DatePicker
                selected={formData.newCheckOutDate}
                onChange={(date) => setFormData({ ...formData, newCheckOutDate: date })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
                placeholderText="Select new check-out date"
                dateFormat="MMM dd, yyyy"
                minDate={formData.newCheckInDate || formData.checkInDate}
                popperPlacement="bottom-start"
                isClearable
                showPopperArrow={false}
                calendarClassName="custom-calendar"
              />
            </div>

            {/* Add-ons / Factors */}
            <div className="md:col-span-2 xl:col-span-3">
              <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                <FaTag className="text-gray-500" />
                <span>Add-ons / Factors</span>
              </label>
              {!factorsLoaded ? (
                <p className="text-sm text-gray-500">Loading add-ons...</p>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
                  {autoFactors.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                        Applied automatically
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {autoFactors.map((f) => (
                          <span
                            key={f.factorid}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                          >
                            {f.factorname}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Priced from the number of guests ({guestCount() || '-'}).
                      </p>
                    </div>
                  )}

                  {lockedFactors.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                        Kept as booked
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {lockedFactors.map((f) => (
                          <span
                            key={f.factorid}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                          >
                            {f.factorname}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {bedTypeFactors.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                        Bed type
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {bedTypeFactors.map((f) => (
                          <label
                            key={f.factorid}
                            className="flex items-center space-x-2 text-sm text-gray-800 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="bedTypeFactor"
                              checked={selectedFactorIds.includes(Number(f.factorid))}
                              onChange={() => toggleFactor(f)}
                            />
                            <span>{f.factorname}</span>
                            <span className="text-gray-500">{factorValueLabel(f)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {addonFactors.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                        Add-ons
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {addonFactors.map((f) => (
                          <label
                            key={f.factorid}
                            className="flex items-center space-x-2 text-sm text-gray-800 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedFactorIds.includes(Number(f.factorid))}
                              onChange={() => toggleFactor(f)}
                            />
                            <span>{f.factorname}</span>
                            <span className="text-gray-500">{factorValueLabel(f)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectableFactors.length === 0 &&
                    lockedFactors.length === 0 &&
                    autoFactors.length === 0 && (
                      <p className="text-sm text-gray-500">This reservation has no add-ons.</p>
                    )}

                  <p className="text-xs text-gray-500">
                    Prices come from the room type. Use Preview Changes to see the effect of adding
                    or removing an add-on.
                  </p>
                </div>
              )}
            </div>

            {/* Availability Check — moved AFTER Add-ons / Factors */}
            <div className="md:col-span-2 xl:col-span-3">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <button
                    type="button"
                    onClick={handleCheckAvailability}
                    disabled={isCheckingAvailability}
                    className="inline-flex items-center justify-center space-x-2 px-5 py-2.5 border border-gray-800 text-gray-800 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                  >
                    <FaCalendarAlt />
                    <span>{isCheckingAvailability ? 'Checking...' : 'Show Availability'}</span>
                  </button>
                  <p className="text-xs text-gray-500">
                    Checks {targetRoomType || 'the room types'} and the other room types for the
                    dates shown above, with the selected add-ons.
                  </p>
                </div>

                {availability && (
                  <div className="mt-4 space-y-3">
                    {availabilityFor(targetRoomType) ? (
                      (() => {
                        const result = availabilityFor(targetRoomType);
                        return (
                          <div
                            className={`rounded-lg border p-4 ${
                              availabilityStyle[result.status] || 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {result.room_type}
                                  {!formData.newRoomType && (
                                    <span className="ml-2 text-xs font-normal text-gray-500">
                                      (current room type)
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-600">
                                  {formatDisplayDate(availability.checkin)} to{' '}
                                  {formatDisplayDate(availability.checkout)}
                                </p>
                              </div>
                              <span className="px-3 py-1 rounded-full text-sm font-semibold bg-white border">
                                {availabilityLabel[result.status] || result.status}
                              </span>
                            </div>

                            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500">
                                  Available rooms
                                </p>
                                <p className="font-semibold text-gray-900">
                                  {result.available_rooms} of {result.total_rooms}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500">
                                  Booked
                                </p>
                                <p className="font-semibold text-gray-900">{result.booked_rooms}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500">
                                  Rate per night
                                </p>
                                <p className="font-semibold text-gray-900">
                                  {formatCurrency(result.final_price)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wider text-gray-500">
                                  Add-ons per night
                                </p>
                                <p className="font-semibold text-gray-900">
                                  {formatCurrency(result.amenities_total)}
                                </p>
                              </div>
                            </div>

                            {result.status_reason && (
                              <p className="mt-3 text-sm text-gray-700">{result.status_reason}</p>
                            )}

                            {result.matched_rooms?.length > 0 && (
                              <div className="mt-3">
                                <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                                  Rooms you can assign
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {result.matched_rooms.slice(0, 20).map((room) => (
                                    <span
                                      key={room.roomid}
                                      className="px-2.5 py-1 bg-white border border-gray-200 rounded-md text-sm text-gray-800"
                                    >
                                      {room.roomnumber}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {!formData.newRoomType && (
                              <p className="mt-3 text-xs text-gray-500">
                                This reservation is counted as booked in its current room type.
                              </p>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                        No availability data for {targetRoomType}.
                      </div>
                    )}

                    {availability.roomTypes.filter(
                      (rt) =>
                        rt.room_type !== targetRoomType &&
                        roomTypes.some(
                          (r) => r.roomtypename === rt.room_type && r.is_active !== false
                        )
                    ).length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                          Other room types
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                          {availability.roomTypes
                            .filter(
                              (rt) =>
                                rt.room_type !== targetRoomType &&
                                roomTypes.some(
                                  (r) => r.roomtypename === rt.room_type && r.is_active !== false
                                )
                            )
                            .map((rt) => (
                              <div
                                key={rt.roomtypeid}
                                className={`rounded-lg border px-3 py-2 text-sm ${
                                  availabilityStyle[rt.status] || 'bg-gray-50 border-gray-200'
                                }`}
                              >
                                <p className="font-semibold text-gray-900">{rt.room_type}</p>
                                <p className="text-xs">
                                  {rt.available_rooms} of {rt.total_rooms} available ·{' '}
                                  {availabilityLabel[rt.status] || rt.status}
                                </p>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-8 pt-6 border-t border-gray-200">
            {isTargetUnavailable && (
              <p className="text-sm text-red-600 sm:mr-auto self-center">
                {unavailableReason} Change the room type, dates, or add-ons to continue.
              </p>
            )}
            <button
              onClick={handlePreview}
              disabled={isLoading || isTargetUnavailable}
              title={isTargetUnavailable ? unavailableReason : ''}
              className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-800 text-gray-800 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-200 w-full sm:w-auto sm:min-w-[180px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaEye />
              <span>{isLoading ? 'Loading...' : 'Preview Changes'}</span>
            </button>
            <button
              onClick={() => setIsConfirmationModalOpen(true)}
              disabled={
                isLoading ||
                isTargetUnavailable ||
                (!formData.newRoomType &&
                  !formData.newCheckInDate &&
                  !formData.newCheckOutDate &&
                  !factorsChanged() &&
                  !guestsChanged())
              }
              title={isTargetUnavailable ? unavailableReason : ''}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200 w-full sm:w-auto sm:min-w-[180px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaCheck />
              <span>Update Reservation</span>
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isConfirmationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FaBed className="text-xl text-gray-700" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Confirm Changes</h2>
            </div>

            <p className="text-gray-600 mb-4">Please review the following changes:</p>

            <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-3">
              {formData.newRoomType && formData.newRoomType !== formData.currentRoomType && (
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-sm text-gray-600">Room Type:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">{formData.currentRoomType}</span>
                    <FaArrowRight className="text-gray-400 text-sm" />
                    <span className="font-semibold text-gray-900">{formData.newRoomType}</span>
                  </div>
                </div>
              )}

              {formData.newCheckInDate && (
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-sm text-gray-600">Check-in:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">
                      {formatDisplayDate(formatDateToYYYYMMDD(formData.checkInDate))}
                    </span>
                    <FaArrowRight className="text-gray-400 text-sm" />
                    <span className="font-semibold text-gray-900">
                      {formatDisplayDate(formatDateToYYYYMMDD(formData.newCheckInDate))}
                    </span>
                  </div>
                </div>
              )}

              {guestsChanged() && (
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-sm text-gray-600">Guests:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">{bookedGuestCount}</span>
                    <FaArrowRight className="text-gray-400 text-sm" />
                    <span className="font-semibold text-gray-900">{guestCount()}</span>
                  </div>
                </div>
              )}

              {factorsChanged() && (
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm text-gray-600">Add-ons:</span>
                  <span className="font-semibold text-gray-900 text-right">
                    {buildSelectedFactors()
                      .map((f) => f.factorname)
                      .join(', ') || 'None'}
                  </span>
                </div>
              )}

              {formData.newCheckOutDate && (
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-sm text-gray-600">Check-out:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">
                      {formatDisplayDate(formatDateToYYYYMMDD(formData.checkOutDate))}
                    </span>
                    <FaArrowRight className="text-gray-400 text-sm" />
                    <span className="font-semibold text-gray-900">
                      {formatDisplayDate(formatDateToYYYYMMDD(formData.newCheckOutDate))}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-3">
              <button
                onClick={() => setIsConfirmationModalOpen(false)}
                className="flex items-center justify-center space-x-2 px-4 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-200"
              >
                <FaTimes />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleConfirm}
                disabled={isLoading}
                className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200 disabled:opacity-50"
              >
                <FaCheck />
                <span>{isLoading ? 'Processing...' : 'Confirm Update'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal - Only Amounts, No Policy Rules */}
      {isPreviewModalOpen && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[95vh]">
            {/* Header - Fixed */}
            <div className="p-4 sm:p-8 pb-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <FaReceipt className="text-xl text-gray-700" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
                    Preview Changes
                  </h2>
                </div>
                {previewData.room_preview?.modification_type && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium flex items-center space-x-1">
                    <FaTag className="text-xs" />
                    <span>{previewData.room_preview.modification_type.toUpperCase()}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="p-4 sm:p-8 pt-4 overflow-y-auto flex-grow">
              <div className="space-y-6">
                {/* Booking Overview */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-600">Reservation Number</p>
                    <p className="text-md font-semibold text-gray-900">{previewData.booking_id}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600">Stay Dates</p>
                    <p className="text-md font-semibold text-gray-900">
                      {formatDisplayDate(previewData.room_preview?.new_checkin)} -{' '}
                      {formatDisplayDate(previewData.room_preview?.new_checkout)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600">Room Type</p>
                    <p className="text-md font-semibold text-gray-900">
                      {previewData.room_preview?.old_room_type}{' '}
                      {previewData.room_preview?.new_room_type &&
                      previewData.room_preview?.new_room_type !==
                        previewData.room_preview?.old_room_type ? (
                        <span className="text-black">
                          {' '}
                          → {previewData.room_preview?.new_room_type}
                        </span>
                      ) : (
                        <span className="text-gray-500 ml-2">(No change)</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Number of nights</p>
                    <p className="text-md text-gray-900 mt-1">
                      {previewData.room_preview?.total_nights} nights stay
                    </p>
                  </div>
                </div>

                {/* Add-ons / Factors */}
                {buildFactorRows(previewData.factors).length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Add-ons / Factors{' '}
                      <span className="text-sm font-normal text-gray-500">(per night)</span>
                    </h3>
                    <div className="overflow-x-auto">
                      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-6 gap-y-2 text-sm min-w-[500px]">
                        <span className="font-semibold text-gray-600">Factor</span>
                        <span className="font-semibold text-gray-600 text-right">Before</span>
                        <span className="font-semibold text-gray-600 text-right">After</span>
                        <span className="font-semibold text-gray-600 text-right">Status</span>
                        {buildFactorRows(previewData.factors).map((row) => (
                          <React.Fragment key={row.id}>
                            <span className="text-gray-900">{row.name}</span>
                            <span
                              className={`text-right ${
                                row.status === 'Removed' || row.status === 'Reduced'
                                  ? 'text-gray-400 line-through'
                                  : 'text-gray-700'
                              }`}
                            >
                              {row.before === null ? '-' : formatCurrency(row.before)}
                            </span>
                            <span className="text-right text-gray-900">
                              {row.after === null ? '-' : formatCurrency(row.after)}
                            </span>
                            <span
                              className={`text-right ${
                                row.status === 'Removed' || row.status === 'Reduced'
                                  ? 'text-green-600'
                                  : row.status === 'Added' || row.status === 'Increased'
                                    ? 'text-red-600'
                                    : 'text-gray-500'
                              }`}
                            >
                              {row.status}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                    {previewData.factors?.net_factors_delta_with_tax !== 0 &&
                      previewData.factors?.net_factors_delta_with_tax !== undefined && (
                        <p className="mt-3 text-sm text-gray-700">
                          Net change from add-ons (for {previewData.factors?.nights} night
                          {previewData.factors?.nights !== 1 ? 's' : ''}, incl. tax):{' '}
                          <span className="font-semibold">
                            {formatCurrency(previewData.factors.net_factors_delta_with_tax)}
                          </span>
                        </p>
                      )}
                  </div>
                )}

                {/* Cost Breakdown */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Cost Breakdown</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-600">Original Room Total</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(previewData.room_preview?.old_total)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-600">New Room Total</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(previewData.room_preview?.new_total)}
                      </span>
                    </div>

                    {previewData.fixed_enhance_services?.total > 0 && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600">Enhance Services</span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(previewData.fixed_enhance_services.total)}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between items-center py-1 border-t border-gray-200 pt-3">
                      <span className="text-gray-900 font-semibold">Price Difference</span>
                      <span className="font-bold text-lg text-gray-900">
                        {previewData.room_preview?.price_difference > 0 ? '+' : ''}
                        {formatCurrency(previewData.room_preview?.price_difference)}
                      </span>
                    </div>

                    {previewData.room_preview?.price_details?.service_fee > 0 && (
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-900 font-semibold">
                          Current Modification Service Fee
                        </span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(previewData.room_preview.price_details.service_fee)}
                        </span>
                      </div>
                    )}
                    {previewData.combined?.service_fee_total_all_modifications > 0 && (
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-900 font-semibold">Total Service Fee</span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(previewData.combined.service_fee_total_all_modifications)}
                        </span>
                      </div>
                    )}
                    {/* Net Financial Impact */}
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">Total Additional Amount</span>
                      <span
                        className={`font-bold text-xl ${
                          previewData.combined?.net_financial_impact > 0
                            ? 'text-green-600'
                            : previewData.combined?.net_financial_impact < 0
                              ? 'text-red-600'
                              : 'text-gray-900'
                        }`}
                      >
                        {previewData.combined?.net_financial_impact > 0 ? '+' : ''}
                        {formatCurrency(previewData.combined?.net_financial_impact)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Existing Services - Only show if there are items */}
                {previewData.service_preview?.existing_items?.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Current Additional Services
                    </h3>
                    <div className="space-y-3">
                      {previewData.service_preview.existing_items.map((item, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                        >
                          <div className="flex items-start space-x-3">
                            <div className="p-1.5 bg-gray-100 rounded-lg">
                              {item.type === 'amenity' ? (
                                <FaHotel className="text-gray-600" />
                              ) : item.type === 'food' ? (
                                <FaUtensils className="text-gray-600" />
                              ) : (
                                <FaReceipt className="text-gray-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{item.name || item.title}</p>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                                  Qty: {item.quantity}
                                </span>
                                {!item.is_refundable && (
                                  <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full flex items-center space-x-1">
                                    <FaShieldAlt className="text-xs" />
                                    <span>Non-refundable</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">
                              {formatCurrency(item.total_amount)}
                            </p>
                            <p className="text-xs text-gray-500">
                              Tax: {formatCurrency(item.tax_amount)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payment Summary */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center rounded-lg">
                      <span className="font-semibold text-gray-800">Total amount</span>
                      <span
                        className={`font-bold text-lg ${
                          previewData.combined?.projected_total_after_preview > 0
                            ? 'text-red-600'
                            : 'text-green-600'
                        }`}
                      >
                        {formatCurrency(previewData.combined?.projected_total_after_preview)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-800">Amount Paid</span>
                      <span className="font-semibold text-green-600">
                        {formatCurrency(previewData.combined?.amount_paid)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-800">Amount Due</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(previewData.combined?.amount_due_after_preview)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-800">Refund Amount</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(previewData.combined?.total_refund)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer - Fixed */}
            <div className="p-4 sm:p-8 pt-4 border-t border-gray-200 flex-shrink-0">
              <div className="flex justify-end">
                <button
                  onClick={() => setIsPreviewModalOpen(false)}
                  className="w-full sm:w-auto px-6 py-2.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Calendar Styles */}
      <style jsx>{`
        .custom-calendar {
          font-family: inherit;
          border-radius: 0.5rem;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
        }
        .react-datepicker {
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
        }
        .react-datepicker__header {
          background-color: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          padding-top: 0.75rem;
        }
        .react-datepicker__day--selected {
          background-color: #1f2937 !important;
          color: white !important;
          border-radius: 0.375rem;
        }
        .react-datepicker__day--keyboard-selected {
          background-color: #374151 !important;
          color: white !important;
          border-radius: 0.375rem;
        }
        .react-datepicker__day:hover {
          background-color: #f3f4f6 !important;
          border-radius: 0.375rem;
        }
      `}</style>
    </div>
  );
};

export default RoomUpgrade;

// import React, { useState, useEffect } from 'react';
// import { NavLink } from 'react-router-dom';
// import { FaArrowLeft, FaHotel } from 'react-icons/fa';
// import DatePicker from 'react-datepicker';
// import 'react-datepicker/dist/react-datepicker.css';
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import {
//   FaBed,
//   FaUser,
//   FaPhone,
//   FaCalendarAlt,
//   FaReceipt,
//   FaEye,
//   FaCheck,
//   FaTimes,
//   FaArrowRight,
//   FaShieldAlt,
//   FaTag,
//   FaCreditCard,
//   FaMoneyBillWave,
//   FaUtensils,
// } from 'react-icons/fa';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const RoomUpgrade = () => {
//   // Get reservation from session storage
//   const reservation = JSON.parse(sessionStorage.getItem('selectedReservation') || '{}');

//   const [formData, setFormData] = useState({
//     fullName: `${reservation?.firstname || ''} ${reservation?.lastname || ''}`.trim(),
//     bookingId: reservation?.bookingid || '',
//     phone: reservation?.phonenumber || '',
//     totalGuests: reservation?.numberofguests || '',
//     currentRoomType: reservation?.roomtypename || '',
//     newRoomType: '',
//     checkInDate: reservation?.checkindate ? new Date(reservation.checkindate) : new Date(),
//     checkOutDate: reservation?.checkoutdate ? new Date(reservation.checkoutdate) : null,
//     newCheckInDate: null,
//     newCheckOutDate: null,
//   });

//   const [roomTypes, setRoomTypes] = useState([]);
//   const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
//   const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
//   const [previewData, setPreviewData] = useState(null);
//   const [isLoading, setIsLoading] = useState(false);

//   // Factors (add-ons) of this reservation, from the modify options API
//   const [appliedFactors, setAppliedFactors] = useState([]);
//   const [availableFactors, setAvailableFactors] = useState([]);
//   const [selectedFactorIds, setSelectedFactorIds] = useState([]);
//   const [factorsLoaded, setFactorsLoaded] = useState(false);

//   // Result of /availability/check-dates/all for the dates and add-ons currently entered
//   const [availability, setAvailability] = useState(null);
//   const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

//   // Fetch room types from roomtypes API (active ones only)
//   useEffect(() => {
//     const fetchRoomTypes = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/roomtypes/`, {
//           method: 'GET',
//           headers: { accept: 'application/json' },
//         });
//         const data = await response.json();
//         // /roomtypes/ returns a plain array (not wrapped in { roomtypes: [...] })
//         setRoomTypes(Array.isArray(data) ? data : data.roomtypes || []);
//       } catch (error) {
//         console.error('Error fetching room types:', error);
//         toast.error('Failed to load room types');
//       }
//     };
//     fetchRoomTypes();
//   }, []);

//   // Fetch the reservation's current factors and the factors that can be selected
//   useEffect(() => {
//     if (!formData.bookingId) return;
//     const fetchFactors = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/modify/${formData.bookingId}/options`, {
//           method: 'GET',
//           headers: { accept: 'application/json' },
//         });
//         const json = await response.json();
//         const options = json.data || {};
//         const applied = options.applied_factors || [];
//         const available = options.available_factors || [];
//         setAppliedFactors(applied);
//         setAvailableFactors(available);
//         // start with the factors the reservation already has (auto factors are not ticked)
//         const selectableIds = available
//           .filter((f) => f.factor_type !== 'auto')
//           .map((f) => Number(f.factorid));
//         setSelectedFactorIds(
//           applied.map((f) => Number(f.factorid)).filter((id) => selectableIds.includes(id))
//         );
//       } catch (error) {
//         console.error('Error fetching factors:', error);
//         toast.error('Failed to load the reservation add-ons');
//       } finally {
//         setFactorsLoaded(true);
//       }
//     };
//     fetchFactors();
//   }, [formData.bookingId]);

//   useEffect(() => {
//     setAvailability(null);
//   }, [formData.newRoomType, formData.newCheckInDate, formData.newCheckOutDate, selectedFactorIds]);

//   const handleChange = (e) => {
//     setFormData({ ...formData, [e.target.name]: e.target.value });
//   };

//   // ── Factors ──────────────────────────────────────────────────────────────────
//   // Auto factors (e.g. Number of Guest) are priced from the guest count by the backend and
//   // are not something staff tick. Applied factors that are not in the selectable list
//   // (inactive / not shown to front desk) are kept as they are so they are never dropped.
//   const availableFactorIdSet = new Set(availableFactors.map((f) => Number(f.factorid)));
//   const autoFactors = appliedFactors.filter((f) => f.factor_type === 'auto');
//   const lockedFactors = appliedFactors.filter(
//     (f) => f.factor_type !== 'auto' && !availableFactorIdSet.has(Number(f.factorid))
//   );
//   const selectableFactors = availableFactors.filter((f) => f.factor_type !== 'auto');
//   const bedTypeFactors = selectableFactors.filter((f) => f.factor_type === 'bedtype');
//   const addonFactors = selectableFactors.filter((f) => f.factor_type !== 'bedtype');

//   const toggleFactor = (factor) => {
//     const id = Number(factor.factorid);
//     setSelectedFactorIds((prev) => {
//       if (factor.factor_type === 'bedtype') {
//         // only one bed type at a time
//         const others = bedTypeFactors.map((f) => Number(f.factorid));
//         return [...prev.filter((x) => !others.includes(x)), id];
//       }
//       return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
//     });
//   };

//   // Values are only a hint: the backend prices every factor from factor_room_type_values.
//   const toFactorPayload = (f, value) => ({
//     factorid: f.factorid,
//     factorname: f.factorname,
//     value,
//     value_type: f.value_type,
//     factor_type: f.factor_type,
//   });

//   const buildPreviousFactors = () =>
//     appliedFactors.map((f) => toFactorPayload(f, f.value_applied ?? 0));

//   const buildSelectedFactors = () => [
//     ...lockedFactors.map((f) => toFactorPayload(f, f.value_applied ?? 0)),
//     ...selectableFactors
//       .filter((f) => selectedFactorIds.includes(Number(f.factorid)))
//       .map((f) => toFactorPayload(f, f.applied_value_for_roomtype ?? 0)),
//   ];

//   const factorsChanged = () => {
//     const before = appliedFactors
//       .filter((f) => f.factor_type !== 'auto' && availableFactorIdSet.has(Number(f.factorid)))
//       .map((f) => Number(f.factorid))
//       .sort();
//     const after = [...selectedFactorIds].sort();
//     return before.length !== after.length || before.some((id, i) => id !== after[i]);
//   };

//   // Guest count the reservation was booked with, and the count staff entered. Guest-based
//   // factors (e.g. Number of Guest) are priced for the entered count.
//   const bookedGuestCount = Number(reservation?.numberofguests) || undefined;

//   const guestCount = () => {
//     const entered = parseInt(formData.totalGuests, 10);
//     return Number.isFinite(entered) && entered > 0 ? entered : bookedGuestCount;
//   };

//   const guestsChanged = () => bookedGuestCount !== undefined && guestCount() !== bookedGuestCount;

//   const factorBody = () => ({
//     selected_factors: buildSelectedFactors(),
//     previous_factors: buildPreviousFactors(),
//     number_of_guests: guestCount(),
//     previous_number_of_guests: bookedGuestCount,
//   });

//   // ── Availability (GET /availability/check-dates/all) ─────────────────────────
//   const targetRoomType = formData.newRoomType || formData.currentRoomType;

//   const availabilityFor = (roomTypeName) =>
//     availability?.roomTypes?.find((rt) => rt.room_type === roomTypeName);

//   const availabilityHint = (roomTypeName) => {
//     const result = availabilityFor(roomTypeName);
//     if (!result) return '';
//     if (result.status === 'AVAILABLE') return ` - ${result.available_rooms} available`;
//     if (result.status === 'NOT_APPLICABLE') return ' - not offered with the selected add-ons';
//     return ' - sold out';
//   };

//   const availabilityStyle = {
//     AVAILABLE: 'bg-green-50 border-green-200 text-green-800',
//     SOLD_OUT: 'bg-red-50 border-red-200 text-red-800',
//     NOT_APPLICABLE: 'bg-yellow-50 border-yellow-200 text-yellow-800',
//   };

//   const availabilityLabel = {
//     AVAILABLE: 'Available',
//     SOLD_OUT: 'Sold out',
//     NOT_APPLICABLE: 'Not offered with the selected add-ons',
//   };

//   const handleCheckAvailability = async () => {
//     const checkin = formatDateToYYYYMMDD(formData.newCheckInDate || formData.checkInDate);
//     const checkout = formatDateToYYYYMMDD(formData.newCheckOutDate || formData.checkOutDate);
//     if (!checkin || !checkout) {
//       toast.error('Select the check-in and check-out dates first');
//       return;
//     }
//     if (checkout <= checkin) {
//       toast.error('Check-out must be after check-in');
//       return;
//     }

//     // factors a room must offer: the ticked add-ons and the ones kept as booked (not auto factors)
//     const factorIds = [...lockedFactors.map((f) => Number(f.factorid)), ...selectedFactorIds];
//     const params = new URLSearchParams({ checkin, checkout });
//     if (factorIds.length > 0) params.set('factor_ids', factorIds.join(','));

//     setIsCheckingAvailability(true);
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/availability/check-dates/all?${params.toString()}`,
//         { method: 'GET', headers: { accept: 'application/json' } }
//       );
//       const data = await response.json();
//       if (response.ok) {
//         setAvailability({ checkin, checkout, roomTypes: data.room_types || [] });
//       } else {
//         toast.error(data.detail || data.message || 'Failed to check availability');
//       }
//     } catch (error) {
//       console.error('Availability error:', error);
//       toast.error('Network error while checking availability');
//     } finally {
//       setIsCheckingAvailability(false);
//     }
//   };

//   const factorValueLabel = (f) => {
//     const value = Number(f.applied_value_for_roomtype ?? 0);
//     if (!value) return '';
//     return f.value_type === 'percentage' ? `${value}%` : formatCurrency(value);
//   };

//   // One row per factor for the preview: what it costs per night before and after
//   const buildFactorRows = (fx) => {
//     const previous = fx?.previous_factors || [];
//     const selected = fx?.selected_factors || [];
//     const same = (a, b) => Number(a.factorid) === Number(b.factorid);
//     const rows = previous.map((p) => {
//       const now = selected.find((s) => same(s, p));
//       const before = Number(p.per_night_amount ?? 0);
//       const after = now ? Number(now.per_night_amount ?? 0) : null;
//       let status = 'Unchanged';
//       if (after === null) status = 'Removed';
//       else if (after < before) status = 'Reduced';
//       else if (after > before) status = 'Increased';
//       return { id: p.factorid, name: p.factorname, before, after, status };
//     });
//     selected
//       .filter((s) => !previous.some((p) => same(s, p)))
//       .forEach((s) =>
//         rows.push({
//           id: s.factorid,
//           name: s.factorname,
//           before: null,
//           after: Number(s.per_night_amount ?? 0),
//           status: 'Added',
//         })
//       );
//     return rows;
//   };

//   // Format date to YYYY-MM-DD without timezone issues
//   const formatDateToYYYYMMDD = (date) => {
//     if (!date) return null;
//     const year = date.getFullYear();
//     const month = String(date.getMonth() + 1).padStart(2, '0');
//     const day = String(date.getDate()).padStart(2, '0');
//     return `${year}-${month}-${day}`;
//   };

//   const handlePreview = async () => {
//     // Check if any changes are made
//     const hasRoomChange = formData.newRoomType && formData.newRoomType !== formData.currentRoomType;
//     const hasCheckInChange =
//       formData.newCheckInDate &&
//       formatDateToYYYYMMDD(formData.newCheckInDate) !== formatDateToYYYYMMDD(formData.checkInDate);
//     const hasCheckOutChange =
//       formData.newCheckOutDate &&
//       formatDateToYYYYMMDD(formData.newCheckOutDate) !==
//         formatDateToYYYYMMDD(formData.checkOutDate);

//     if (
//       !hasRoomChange &&
//       !hasCheckInChange &&
//       !hasCheckOutChange &&
//       !factorsChanged() &&
//       !guestsChanged()
//     ) {
//       toast.error(
//         'Please make at least one change (room type, dates, number of guests, or add-ons)'
//       );
//       return;
//     }

//     setIsLoading(true);
//     try {
//       const requestBody = {
//         new_checkin_date: hasCheckInChange
//           ? formatDateToYYYYMMDD(formData.newCheckInDate)
//           : formatDateToYYYYMMDD(formData.checkInDate),
//         new_checkout_date: hasCheckOutChange
//           ? formatDateToYYYYMMDD(formData.newCheckOutDate)
//           : formatDateToYYYYMMDD(formData.checkOutDate),
//         amenities: [],
//         room_services: [],
//         food_orders: [],
//         ...factorBody(),
//       };

//       // ALWAYS send the room type - either new one if changed, or current one if not changed
//       requestBody.new_room_type_name = hasRoomChange
//         ? formData.newRoomType
//         : formData.currentRoomType;

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/modify/${formData.bookingId}/preview`, {
//         method: 'POST',
//         headers: {
//           accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(requestBody),
//       });

//       const responseData = await response.json();

//       if (response.ok) {
//         setPreviewData(responseData.data);
//         setIsPreviewModalOpen(true);
//       } else {
//         toast.error(responseData.detail || responseData.message || 'Failed to fetch preview');
//       }
//     } catch (error) {
//       console.error('Preview error:', error);
//       toast.error('Network error while fetching preview data');
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleConfirm = async () => {
//     setIsConfirmationModalOpen(false);
//     setIsLoading(true);

//     try {
//       const params = new URLSearchParams();

//       params.append(
//         'new_checkin_date',
//         formData.newCheckInDate
//           ? formatDateToYYYYMMDD(formData.newCheckInDate)
//           : formatDateToYYYYMMDD(formData.checkInDate)
//       );
//       params.append(
//         'new_checkout_date',
//         formData.newCheckOutDate
//           ? formatDateToYYYYMMDD(formData.newCheckOutDate)
//           : formatDateToYYYYMMDD(formData.checkOutDate)
//       );

//       // ALWAYS send the room type - either new one if changed, or current one if not changed
//       params.append(
//         'new_room_type_name',
//         formData.newRoomType && formData.newRoomType !== formData.currentRoomType
//           ? formData.newRoomType
//           : formData.currentRoomType
//       );

//       const url = `${BQ_BASE_URL}/bq/api/modify/${formData.bookingId}/modify?${params.toString()}`;

//       const response = await fetch(url, {
//         method: 'POST',
//         headers: {
//           accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(factorBody()),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         // Clear the selected reservation from session storage
//         sessionStorage.removeItem('selectedReservation');

//         // Show success toast with the message from API
//         toast.success(data.message || 'Reservation modified successfully', {
//           onClose: () => {
//             // Navigate back to front desk operations after toast closes
//             window.location.href =
//               '/dashboard/core-modules/property-management-system/front-desk-operations';
//           },
//         });

//         // Reset form
//         setFormData((prev) => ({
//           ...prev,
//           newRoomType: '',
//           newCheckInDate: null,
//           newCheckOutDate: null,
//         }));
//       } else {
//         toast.error(data.detail || data.message || 'Failed to modify reservation');
//       }
//     } catch (error) {
//       console.error('Modification error:', error);
//       toast.error('An error occurred while modifying the reservation');
//     } finally {
//       setIsLoading(false);
//     }
//   };
//   const formatCurrency = (amount) => {
//     if (!amount && amount !== 0) return '₹0.00';
//     return new Intl.NumberFormat('en-IN', {
//       style: 'currency',
//       currency: 'INR',
//       minimumFractionDigits: 2,
//     }).format(amount);
//   };

//   const formatDisplayDate = (dateString) => {
//     if (!dateString) return 'N/A';
//     try {
//       // Handle YYYY-MM-DD format directly
//       if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
//         const [year, month, day] = dateString.split('-');
//         const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
//         return date.toLocaleDateString('en-US', {
//           year: 'numeric',
//           month: 'short',
//           day: 'numeric',
//         });
//       }
//       return new Date(dateString).toLocaleDateString('en-US', {
//         weekday: 'short',
//         year: 'numeric',
//         month: 'short',
//         day: 'numeric',
//       });
//     } catch {
//       return dateString;
//     }
//   };

//   // Don't render if no booking selected
//   if (!formData.bookingId) {
//     return (
//       <div className="p-6 bg-white min-h-screen">
//         <div className="max-w-6xl mx-auto mt-8 p-8 bg-yellow-50 rounded-lg text-center">
//           <p className="text-yellow-800 font-semibold">
//             No reservation selected. Please select a reservation from the room management page.
//           </p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="p-6 bg-white min-h-screen">
//       <ToastContainer
//         position="top-right"
//         autoClose={5000}
//         hideProgressBar={false}
//         newestOnTop={false}
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//         theme="light"
//       />

//       <NavLink
//         to="/dashboard/core-modules/property-management-system/front-desk-operations"
//         className="flex items-center text-gray-400 hover:text-black mb-4"
//       >
//         <FaArrowLeft className="mr-3 text-md" />
//         <span className="text-lg">Back to Front Desk Operations</span>
//       </NavLink>

//       {/* Header Section */}
//       <header className="text-center mb-8 mt-6">
//         <div className="flex items-center justify-center space-x-3 mb-3">
//           <div className="p-2 bg-gray-100 rounded-lg">
//             <FaBed className="text-2xl text-gray-700" />
//           </div>
//           <h1 className="text-3xl font-semibold text-gray-900">Modify Reservation</h1>
//         </div>
//         <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
//           Update room type, check-in date, or check-out date. Preview all changes before confirming.
//         </p>
//         <div className="w-24 h-1 bg-gray-300 mx-auto mt-4 rounded-full"></div>
//       </header>

//       {/* Main Form Section */}
//       <div className="max-w-6xl mx-auto bg-gray-50 rounded-xl shadow-sm border border-gray-200 p-8">
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//           {/* Guest Information */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
//               <FaUser className="text-gray-500" />
//               <span>Guest Name</span>
//             </label>
//             <input
//               value={formData.fullName}
//               readOnly
//               className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
//             />
//           </div>

//           {/* Reservation Number */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold text-gray-700 mb-2">Reservation Number</label>
//             <input
//               value={formData.bookingId}
//               readOnly
//               className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
//             />
//           </div>

//           {/* Phone Number */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
//               <FaPhone className="text-gray-500" />
//               <span>Phone Number</span>
//             </label>
//             <input
//               name="phone"
//               value={formData.phone}
//               onChange={handleChange}
//               className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
//             />
//           </div>

//           {/* Number of Guests */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold text-gray-700 mb-2">Number of Guests</label>
//             <input
//               name="totalGuests"
//               type="number"
//               min="1"
//               value={formData.totalGuests}
//               onChange={handleChange}
//               className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
//             />
//             <p className="text-xs text-gray-500 mt-1">
//               Used to price guest-based add-ons in the preview.
//             </p>
//           </div>

//           {/* Current Room Type */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
//               <FaBed className="text-gray-500" />
//               <span>Current Room Type</span>
//             </label>
//             <input
//               value={formData.currentRoomType}
//               readOnly
//               className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
//             />
//           </div>

//           {/* New Room Type - Optional */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold text-gray-700 mb-2">
//               New Room Type <span className="text-gray-400 text-xs">(Optional)</span>
//             </label>
//             <select
//               name="newRoomType"
//               value={formData.newRoomType}
//               onChange={handleChange}
//               className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
//             >
//               <option value="">Same Room Type</option>
//               {roomTypes
//                 .filter((rt) => rt.is_active !== false)
//                 .filter((rt) => rt.roomtypename !== formData.currentRoomType)
//                 .map((roomType) => (
//                   <option key={roomType.roomtypeid} value={roomType.roomtypename}>
//                     {roomType.roomtypename}
//                     {availabilityHint(roomType.roomtypename)}
//                   </option>
//                 ))}
//             </select>
//           </div>

//           {/* Room availability for the entered dates and add-ons */}
//           <div className="md:col-span-2">
//             <div className="flex flex-col sm:flex-row sm:items-center gap-3">
//               <button
//                 type="button"
//                 onClick={handleCheckAvailability}
//                 disabled={isCheckingAvailability}
//                 className="flex items-center justify-center space-x-2 px-5 py-2.5 border border-gray-800 text-gray-800 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
//               >
//                 <FaCalendarAlt />
//                 <span>{isCheckingAvailability ? 'Checking...' : 'Show Availability'}</span>
//               </button>
//               <p className="text-xs text-gray-500">
//                 Checks {targetRoomType || 'the room types'} and the other room types for the dates
//                 shown below, with the selected add-ons.
//               </p>
//             </div>

//             {availability && (
//               <div className="mt-4 space-y-3">
//                 {availabilityFor(targetRoomType) ? (
//                   (() => {
//                     const result = availabilityFor(targetRoomType);
//                     return (
//                       <div
//                         className={`rounded-lg border p-4 ${
//                           availabilityStyle[result.status] || 'bg-gray-50 border-gray-200'
//                         }`}
//                       >
//                         <div className="flex flex-wrap items-center justify-between gap-2">
//                           <div>
//                             <p className="font-semibold text-gray-900">
//                               {result.room_type}
//                               {!formData.newRoomType && (
//                                 <span className="ml-2 text-xs font-normal text-gray-500">
//                                   (current room type)
//                                 </span>
//                               )}
//                             </p>
//                             <p className="text-xs text-gray-600">
//                               {formatDisplayDate(availability.checkin)} to{' '}
//                               {formatDisplayDate(availability.checkout)}
//                             </p>
//                           </div>
//                           <span className="px-3 py-1 rounded-full text-sm font-semibold bg-white border">
//                             {availabilityLabel[result.status] || result.status}
//                           </span>
//                         </div>

//                         <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
//                           <div>
//                             <p className="text-xs uppercase tracking-wider text-gray-500">
//                               Available rooms
//                             </p>
//                             <p className="font-semibold text-gray-900">
//                               {result.available_rooms} of {result.total_rooms}
//                             </p>
//                           </div>
//                           <div>
//                             <p className="text-xs uppercase tracking-wider text-gray-500">Booked</p>
//                             <p className="font-semibold text-gray-900">{result.booked_rooms}</p>
//                           </div>
//                           <div>
//                             <p className="text-xs uppercase tracking-wider text-gray-500">
//                               Rate per night
//                             </p>
//                             <p className="font-semibold text-gray-900">
//                               {formatCurrency(result.final_price)}
//                             </p>
//                           </div>
//                           <div>
//                             <p className="text-xs uppercase tracking-wider text-gray-500">
//                               Add-ons per night
//                             </p>
//                             <p className="font-semibold text-gray-900">
//                               {formatCurrency(result.amenities_total)}
//                             </p>
//                           </div>
//                         </div>

//                         {result.status_reason && (
//                           <p className="mt-3 text-sm text-gray-700">{result.status_reason}</p>
//                         )}

//                         {result.matched_rooms?.length > 0 && (
//                           <div className="mt-3">
//                             <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
//                               Rooms you can assign
//                             </p>
//                             <div className="flex flex-wrap gap-2">
//                               {result.matched_rooms.slice(0, 20).map((room) => (
//                                 <span
//                                   key={room.roomid}
//                                   className="px-2.5 py-1 bg-white border border-gray-200 rounded-md text-sm text-gray-800"
//                                 >
//                                   {room.roomnumber}
//                                 </span>
//                               ))}
//                             </div>
//                           </div>
//                         )}

//                         {!formData.newRoomType && (
//                           <p className="mt-3 text-xs text-gray-500">
//                             This reservation is counted as booked in its current room type.
//                           </p>
//                         )}
//                       </div>
//                     );
//                   })()
//                 ) : (
//                   <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
//                     No availability data for {targetRoomType}.
//                   </div>
//                 )}

//                 {availability.roomTypes.filter(
//                   (rt) =>
//                     rt.room_type !== targetRoomType &&
//                     roomTypes.some((r) => r.roomtypename === rt.room_type && r.is_active !== false)
//                 ).length > 0 && (
//                   <div>
//                     <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
//                       Other room types
//                     </p>
//                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
//                       {availability.roomTypes
//                         .filter(
//                           (rt) =>
//                             rt.room_type !== targetRoomType &&
//                             roomTypes.some(
//                               (r) => r.roomtypename === rt.room_type && r.is_active !== false
//                             )
//                         )
//                         .map((rt) => (
//                           <div
//                             key={rt.roomtypeid}
//                             className={`rounded-lg border px-3 py-2 text-sm ${
//                               availabilityStyle[rt.status] || 'bg-gray-50 border-gray-200'
//                             }`}
//                           >
//                             <p className="font-semibold text-gray-900">{rt.room_type}</p>
//                             <p className="text-xs">
//                               {rt.available_rooms} of {rt.total_rooms} available ·{' '}
//                               {availabilityLabel[rt.status] || rt.status}
//                             </p>
//                           </div>
//                         ))}
//                     </div>
//                   </div>
//                 )}
//               </div>
//             )}
//           </div>

//           {/* Current Check-In Date */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold text-gray-700 mb-2">
//               Current Check-In Date
//             </label>
//             <input
//               type="text"
//               value={formatDisplayDate(formatDateToYYYYMMDD(formData.checkInDate))}
//               readOnly
//               className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
//             />
//           </div>

//           {/* New Check-In Date - Optional */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold text-gray-700 mb-2">
//               New Check-In Date <span className="text-gray-400 text-xs">(Optional)</span>
//             </label>
//             <DatePicker
//               selected={formData.newCheckInDate}
//               onChange={(date) => setFormData({ ...formData, newCheckInDate: date })}
//               className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
//               placeholderText="Select new check-in date"
//               dateFormat="MMM dd, yyyy"
//               minDate={new Date()}
//               maxDate={formData.newCheckOutDate || formData.checkOutDate}
//               popperPlacement="bottom-start"
//               isClearable
//               showPopperArrow={false}
//               calendarClassName="custom-calendar"
//             />
//           </div>

//           {/* Current Check-Out Date */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold text-gray-700 mb-2">
//               Current Check-Out Date
//             </label>
//             <input
//               type="text"
//               value={formatDisplayDate(formatDateToYYYYMMDD(formData.checkOutDate))}
//               readOnly
//               className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
//             />
//           </div>

//           {/* New Check-Out Date - Optional */}
//           <div className="flex flex-col">
//             <label className="text-sm font-semibold text-gray-700 mb-2">
//               New Check-Out Date <span className="text-gray-400 text-xs">(Optional)</span>
//             </label>
//             <DatePicker
//               selected={formData.newCheckOutDate}
//               onChange={(date) => setFormData({ ...formData, newCheckOutDate: date })}
//               className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
//               placeholderText="Select new check-out date"
//               dateFormat="MMM dd, yyyy"
//               minDate={formData.newCheckInDate || formData.checkInDate}
//               popperPlacement="bottom-start"
//               isClearable
//               showPopperArrow={false}
//               calendarClassName="custom-calendar"
//             />
//           </div>

//           {/* Add-ons / Factors */}
//           <div className="md:col-span-2">
//             <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
//               <FaTag className="text-gray-500" />
//               <span>Add-ons / Factors</span>
//             </label>
//             {!factorsLoaded ? (
//               <p className="text-sm text-gray-500">Loading add-ons...</p>
//             ) : (
//               <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
//                 {autoFactors.length > 0 && (
//                   <div>
//                     <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
//                       Applied automatically
//                     </p>
//                     <div className="flex flex-wrap gap-2">
//                       {autoFactors.map((f) => (
//                         <span
//                           key={f.factorid}
//                           className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
//                         >
//                           {f.factorname}
//                         </span>
//                       ))}
//                     </div>
//                     <p className="text-xs text-gray-500 mt-1">
//                       Priced from the number of guests ({guestCount() || '-'}).
//                     </p>
//                   </div>
//                 )}

//                 {lockedFactors.length > 0 && (
//                   <div>
//                     <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
//                       Kept as booked
//                     </p>
//                     <div className="flex flex-wrap gap-2">
//                       {lockedFactors.map((f) => (
//                         <span
//                           key={f.factorid}
//                           className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
//                         >
//                           {f.factorname}
//                         </span>
//                       ))}
//                     </div>
//                   </div>
//                 )}

//                 {bedTypeFactors.length > 0 && (
//                   <div>
//                     <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
//                       Bed type
//                     </p>
//                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
//                       {bedTypeFactors.map((f) => (
//                         <label
//                           key={f.factorid}
//                           className="flex items-center space-x-2 text-sm text-gray-800 cursor-pointer"
//                         >
//                           <input
//                             type="radio"
//                             name="bedTypeFactor"
//                             checked={selectedFactorIds.includes(Number(f.factorid))}
//                             onChange={() => toggleFactor(f)}
//                           />
//                           <span>{f.factorname}</span>
//                           <span className="text-gray-500">{factorValueLabel(f)}</span>
//                         </label>
//                       ))}
//                     </div>
//                   </div>
//                 )}

//                 {addonFactors.length > 0 && (
//                   <div>
//                     <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
//                       Add-ons
//                     </p>
//                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
//                       {addonFactors.map((f) => (
//                         <label
//                           key={f.factorid}
//                           className="flex items-center space-x-2 text-sm text-gray-800 cursor-pointer"
//                         >
//                           <input
//                             type="checkbox"
//                             checked={selectedFactorIds.includes(Number(f.factorid))}
//                             onChange={() => toggleFactor(f)}
//                           />
//                           <span>{f.factorname}</span>
//                           <span className="text-gray-500">{factorValueLabel(f)}</span>
//                         </label>
//                       ))}
//                     </div>
//                   </div>
//                 )}

//                 {selectableFactors.length === 0 &&
//                   lockedFactors.length === 0 &&
//                   autoFactors.length === 0 && (
//                     <p className="text-sm text-gray-500">This reservation has no add-ons.</p>
//                   )}

//                 <p className="text-xs text-gray-500">
//                   Prices come from the room type. Use Preview Changes to see the effect of adding or
//                   removing an add-on.
//                 </p>
//               </div>
//             )}
//           </div>
//         </div>

//         {/* Action Buttons */}
//         <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
//           <button
//             onClick={handlePreview}
//             disabled={isLoading}
//             className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-800 text-gray-800 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-200 min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             <FaEye />
//             <span>{isLoading ? 'Loading...' : 'Preview Changes'}</span>
//           </button>
//           <button
//             onClick={() => setIsConfirmationModalOpen(true)}
//             disabled={
//               isLoading ||
//               (!formData.newRoomType &&
//                 !formData.newCheckInDate &&
//                 !formData.newCheckOutDate &&
//                 !factorsChanged() &&
//                 !guestsChanged())
//             }
//             className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200 min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
//           >
//             <FaCheck />
//             <span>Update Reservation</span>
//           </button>
//         </div>
//       </div>

//       {/* Confirmation Modal */}
//       {isConfirmationModalOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full">
//             <div className="flex items-center space-x-3 mb-4">
//               <div className="p-2 bg-gray-100 rounded-lg">
//                 <FaBed className="text-xl text-gray-700" />
//               </div>
//               <h2 className="text-xl font-semibold text-gray-900">Confirm Changes</h2>
//             </div>

//             <p className="text-gray-600 mb-4">Please review the following changes:</p>

//             <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-3">
//               {formData.newRoomType && formData.newRoomType !== formData.currentRoomType && (
//                 <div className="flex items-center justify-between">
//                   <span className="text-sm text-gray-600">Room Type:</span>
//                   <div className="flex items-center space-x-2">
//                     <span className="font-medium text-gray-900">{formData.currentRoomType}</span>
//                     <FaArrowRight className="text-gray-400 text-sm" />
//                     <span className="font-semibold text-gray-900">{formData.newRoomType}</span>
//                   </div>
//                 </div>
//               )}

//               {formData.newCheckInDate && (
//                 <div className="flex items-center justify-between">
//                   <span className="text-sm text-gray-600">Check-in:</span>
//                   <div className="flex items-center space-x-2">
//                     <span className="font-medium text-gray-900">
//                       {formatDisplayDate(formatDateToYYYYMMDD(formData.checkInDate))}
//                     </span>
//                     <FaArrowRight className="text-gray-400 text-sm" />
//                     <span className="font-semibold text-gray-900">
//                       {formatDisplayDate(formatDateToYYYYMMDD(formData.newCheckInDate))}
//                     </span>
//                   </div>
//                 </div>
//               )}

//               {guestsChanged() && (
//                 <div className="flex items-center justify-between">
//                   <span className="text-sm text-gray-600">Guests:</span>
//                   <div className="flex items-center space-x-2">
//                     <span className="font-medium text-gray-900">{bookedGuestCount}</span>
//                     <FaArrowRight className="text-gray-400 text-sm" />
//                     <span className="font-semibold text-gray-900">{guestCount()}</span>
//                   </div>
//                 </div>
//               )}

//               {factorsChanged() && (
//                 <div className="flex items-start justify-between">
//                   <span className="text-sm text-gray-600">Add-ons:</span>
//                   <span className="font-semibold text-gray-900 text-right">
//                     {buildSelectedFactors()
//                       .map((f) => f.factorname)
//                       .join(', ') || 'None'}
//                   </span>
//                 </div>
//               )}

//               {formData.newCheckOutDate && (
//                 <div className="flex items-center justify-between">
//                   <span className="text-sm text-gray-600">Check-out:</span>
//                   <div className="flex items-center space-x-2">
//                     <span className="font-medium text-gray-900">
//                       {formatDisplayDate(formatDateToYYYYMMDD(formData.checkOutDate))}
//                     </span>
//                     <FaArrowRight className="text-gray-400 text-sm" />
//                     <span className="font-semibold text-gray-900">
//                       {formatDisplayDate(formatDateToYYYYMMDD(formData.newCheckOutDate))}
//                     </span>
//                   </div>
//                 </div>
//               )}
//             </div>

//             <div className="flex justify-end space-x-3">
//               <button
//                 onClick={() => setIsConfirmationModalOpen(false)}
//                 className="flex items-center space-x-2 px-4 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-200"
//               >
//                 <FaTimes />
//                 <span>Cancel</span>
//               </button>
//               <button
//                 onClick={handleConfirm}
//                 disabled={isLoading}
//                 className="flex items-center space-x-2 px-4 py-2.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200 disabled:opacity-50"
//               >
//                 <FaCheck />
//                 <span>{isLoading ? 'Processing...' : 'Confirm Update'}</span>
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Preview Modal - Only Amounts, No Policy Rules */}
//       {isPreviewModalOpen && previewData && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
//           <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full flex flex-col max-h-[90vh]">
//             {/* Header - Fixed */}
//             <div className="p-8 pb-4 border-b border-gray-200 flex-shrink-0">
//               <div className="flex items-center justify-between">
//                 <div className="flex items-center space-x-3">
//                   <div className="p-2 bg-gray-100 rounded-lg">
//                     <FaReceipt className="text-xl text-gray-700" />
//                   </div>
//                   <h2 className="text-2xl font-semibold text-gray-900">Preview Changes</h2>
//                 </div>
//                 {previewData.room_preview?.modification_type && (
//                   <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium flex items-center space-x-1">
//                     <FaTag className="text-xs" />
//                     <span>{previewData.room_preview.modification_type.toUpperCase()}</span>
//                   </span>
//                 )}
//               </div>
//             </div>

//             {/* Content - Scrollable */}
//             <div className="p-8 pt-4 overflow-y-auto flex-grow">
//               <div className="space-y-6">
//                 {/* Booking Overview */}
//                 <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
//                   <div className="space-y-2">
//                     <div>
//                       <p className="text-sm font-semibold text-gray-600">Reservation Number</p>
//                       <p className="text-md font-semibold text-gray-900">
//                         {previewData.booking_id}
//                       </p>
//                     </div>
//                   </div>
//                   <div className="space-y-4">
//                     <div>
//                       <p className="text-sm font-semibold text-gray-600">Stay Dates</p>
//                       <p className="text-md font-semibold text-gray-900">
//                         {formatDisplayDate(previewData.room_preview?.new_checkin)} -{' '}
//                         {formatDisplayDate(previewData.room_preview?.new_checkout)}
//                       </p>
//                     </div>
//                   </div>
//                   <div>
//                     <p className="text-sm font-semibold text-gray-600">Room Type</p>
//                     <p className="text-md font-semibold text-gray-900">
//                       {previewData.room_preview?.old_room_type}{' '}
//                       {previewData.room_preview?.new_room_type &&
//                       previewData.room_preview?.new_room_type !==
//                         previewData.room_preview?.old_room_type ? (
//                         <>
//                           <span className="text-black">
//                             {previewData.room_preview?.new_room_type}
//                           </span>
//                         </>
//                       ) : (
//                         <span className="text-gray-500 ml-2">(No change)</span>
//                       )}
//                     </p>
//                   </div>
//                   <div>
//                     <p className="text-sm font-semibold text-gray-800">Number of nights</p>
//                     <p className="text-md text-gray-900 mt-1">
//                       {previewData.room_preview?.total_nights} nights stay
//                     </p>
//                   </div>
//                 </div>

//                 {/* Add-ons / Factors */}
//                 {buildFactorRows(previewData.factors).length > 0 && (
//                   <div className="border-t border-gray-200 pt-6">
//                     <h3 className="text-lg font-semibold text-gray-900 mb-2">
//                       Add-ons / Factors{' '}
//                       <span className="text-sm font-normal text-gray-500">(per night)</span>
//                     </h3>
//                     <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-6 gap-y-2 text-sm">
//                       <span className="font-semibold text-gray-600">Factor</span>
//                       <span className="font-semibold text-gray-600 text-right">Before</span>
//                       <span className="font-semibold text-gray-600 text-right">After</span>
//                       <span className="font-semibold text-gray-600 text-right">Status</span>
//                       {buildFactorRows(previewData.factors).map((row) => (
//                         <React.Fragment key={row.id}>
//                           <span className="text-gray-900">{row.name}</span>
//                           <span
//                             className={`text-right ${
//                               row.status === 'Removed' || row.status === 'Reduced'
//                                 ? 'text-gray-400 line-through'
//                                 : 'text-gray-700'
//                             }`}
//                           >
//                             {row.before === null ? '-' : formatCurrency(row.before)}
//                           </span>
//                           <span className="text-right text-gray-900">
//                             {row.after === null ? '-' : formatCurrency(row.after)}
//                           </span>
//                           <span
//                             className={`text-right ${
//                               row.status === 'Removed' || row.status === 'Reduced'
//                                 ? 'text-green-600'
//                                 : row.status === 'Added' || row.status === 'Increased'
//                                   ? 'text-red-600'
//                                   : 'text-gray-500'
//                             }`}
//                           >
//                             {row.status}
//                           </span>
//                         </React.Fragment>
//                       ))}
//                     </div>
//                     {previewData.factors?.net_factors_delta_with_tax !== 0 &&
//                       previewData.factors?.net_factors_delta_with_tax !== undefined && (
//                         <p className="mt-3 text-sm text-gray-700">
//                           Net change from add-ons (for {previewData.factors?.nights} night
//                           {previewData.factors?.nights !== 1 ? 's' : ''}, incl. tax):{' '}
//                           <span className="font-semibold">
//                             {formatCurrency(previewData.factors.net_factors_delta_with_tax)}
//                           </span>
//                         </p>
//                       )}
//                   </div>
//                 )}

//                 {/* Cost Breakdown */}
//                 <div className="border-t border-gray-200 pt-6">
//                   <h3 className="text-lg font-semibold text-gray-900 mb-2">Cost Breakdown</h3>
//                   <div className="space-y-2">
//                     <div className="flex justify-between items-center py-1">
//                       <span className="text-gray-600">Original Room Total</span>
//                       <span className="font-semibold text-gray-900">
//                         {formatCurrency(previewData.room_preview?.old_total)}
//                       </span>
//                     </div>
//                     <div className="flex justify-between items-center py-1">
//                       <span className="text-gray-600">New Room Total</span>
//                       <span className="font-semibold text-gray-900">
//                         {formatCurrency(previewData.room_preview?.new_total)}
//                       </span>
//                     </div>

//                     {previewData.fixed_enhance_services?.total > 0 && (
//                       <div className="flex justify-between items-center py-2">
//                         <span className="text-gray-600">Enhance Services</span>
//                         <span className="font-semibold text-gray-900">
//                           {formatCurrency(previewData.fixed_enhance_services.total)}
//                         </span>
//                       </div>
//                     )}

//                     <div className="flex justify-between items-center py-1 border-t border-gray-200 pt-3">
//                       <span className="text-gray-900 font-semibold">Price Difference</span>
//                       <span
//                         className={`font-bold text-lg ${
//                           previewData.room_preview?.price_difference > 0
//                             ? 'text-gray-900'
//                             : previewData.room_preview?.price_difference < 0
//                               ? 'text-gray-900'
//                               : 'text-gray-900'
//                         }`}
//                       >
//                         {previewData.room_preview?.price_difference > 0 ? '+' : ''}
//                         {formatCurrency(previewData.room_preview?.price_difference)}
//                       </span>
//                     </div>

//                     {previewData.room_preview?.price_details?.service_fee > 0 && (
//                       <div className="flex justify-between items-center py-1">
//                         <span className="text-gray-900 font-semibold">
//                           Current Modification Service Fee
//                         </span>
//                         <span className="font-semibold text-gray-900">
//                           {formatCurrency(previewData.room_preview.price_details.service_fee)}
//                         </span>
//                       </div>
//                     )}
//                     {previewData.combined?.service_fee_total_all_modifications > 0 && (
//                       <div className="flex justify-between items-center py-1">
//                         <span className="text-gray-900 font-semibold">Total Service Fee</span>
//                         <span className="font-semibold text-gray-900">
//                           {formatCurrency(previewData.combined.service_fee_total_all_modifications)}
//                         </span>
//                       </div>
//                     )}
//                     {/* Net Financial Impact */}
//                     <div className="flex justify-between items-center ">
//                       <span className="font-semibold text-gray-900">Total Additional Amount</span>
//                       <span
//                         className={`font-bold text-xl ${
//                           previewData.combined?.net_financial_impact > 0
//                             ? 'text-green-600'
//                             : previewData.combined?.net_financial_impact < 0
//                               ? 'text-red-600'
//                               : 'text-gray-900'
//                         }`}
//                       >
//                         {previewData.combined?.net_financial_impact > 0 ? '+' : ''}
//                         {formatCurrency(previewData.combined?.net_financial_impact)}
//                       </span>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Existing Services - Only show if there are items */}
//                 {previewData.service_preview?.existing_items?.length > 0 && (
//                   <div className="border-t border-gray-200 pt-6">
//                     <h3 className="text-lg font-semibold text-gray-900 mb-4">
//                       Current Additional Services
//                     </h3>
//                     <div className="space-y-3">
//                       {previewData.service_preview.existing_items.map((item, index) => (
//                         <div
//                           key={index}
//                           className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
//                         >
//                           <div className="flex items-start space-x-3">
//                             <div className="p-1.5 bg-gray-100 rounded-lg">
//                               {item.type === 'amenity' ? (
//                                 <FaHotel className="text-gray-600" />
//                               ) : item.type === 'food' ? (
//                                 <FaUtensils className="text-gray-600" />
//                               ) : (
//                                 <FaReceipt className="text-gray-600" />
//                               )}
//                             </div>
//                             <div>
//                               <p className="font-medium text-gray-900">{item.name || item.title}</p>
//                               <div className="flex items-center space-x-2 mt-1">
//                                 <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
//                                   Qty: {item.quantity}
//                                 </span>
//                                 {!item.is_refundable && (
//                                   <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full flex items-center space-x-1">
//                                     <FaShieldAlt className="text-xs" />
//                                     <span>Non-refundable</span>
//                                   </span>
//                                 )}
//                               </div>
//                             </div>
//                           </div>
//                           <div className="text-right">
//                             <p className="font-semibold text-gray-900">
//                               {formatCurrency(item.total_amount)}
//                             </p>
//                             <p className="text-xs text-gray-500">
//                               Tax: {formatCurrency(item.tax_amount)}
//                             </p>
//                           </div>
//                         </div>
//                       ))}
//                     </div>
//                   </div>
//                 )}

//                 {/* Payment Summary */}
//                 <div className="border-t border-gray-200 pt-6">
//                   <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h3>
//                   <div className="space-y-3">
//                     <div className="flex justify-between items-center rounded-lg">
//                       <span className="font-semibold text-gray-800">Total amount</span>
//                       <span
//                         className={`font-bold text-lg ${
//                           previewData.combined?.projected_total_after_preview > 0
//                             ? 'text-red-600'
//                             : 'text-green-600'
//                         }`}
//                       >
//                         {formatCurrency(previewData.combined?.projected_total_after_preview)}
//                       </span>
//                     </div>
//                     <div className="flex justify-between items-center py-1">
//                       <span className="text-gray-800">Amount Paid</span>
//                       <span className="font-semibold text-green-600">
//                         {formatCurrency(previewData.combined?.amount_paid)}
//                       </span>
//                     </div>
//                     <div className="flex justify-between items-center py-1">
//                       <span className="text-gray-800">Amount Due</span>
//                       <span className="font-semibold text-gray-900">
//                         {formatCurrency(previewData.combined?.amount_due_after_preview)}
//                       </span>
//                     </div>
//                     <div className="flex justify-between items-center py-1">
//                       <span className="text-gray-800">Refund Amount</span>
//                       <span className="font-semibold text-gray-900">
//                         {formatCurrency(previewData.combined?.total_refund)}
//                       </span>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Footer - Fixed */}
//             <div className="p-8 pt-4 border-t border-gray-200 flex-shrink-0">
//               <div className="flex justify-end">
//                 <button
//                   onClick={() => setIsPreviewModalOpen(false)}
//                   className="px-6 py-2.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200"
//                 >
//                   Close Preview
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Custom Calendar Styles */}
//       <style jsx>{`
//         .custom-calendar {
//           font-family: inherit;
//           border-radius: 0.5rem;
//           box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
//         }
//         .react-datepicker {
//           border: 1px solid #e5e7eb;
//           border-radius: 0.5rem;
//         }
//         .react-datepicker__header {
//           background-color: #f9fafb;
//           border-bottom: 1px solid #e5e7eb;
//           border-top-left-radius: 0.5rem;
//           border-top-right-radius: 0.5rem;
//           padding-top: 0.75rem;
//         }
//         .react-datepicker__day--selected {
//           background-color: #1f2937 !important;
//           color: white !important;
//           border-radius: 0.375rem;
//         }
//         .react-datepicker__day--keyboard-selected {
//           background-color: #374151 !important;
//           color: white !important;
//           border-radius: 0.375rem;
//         }
//         .react-datepicker__day:hover {
//           background-color: #f3f4f6 !important;
//           border-radius: 0.375rem;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default RoomUpgrade;
